import API_BASE from '../../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = API_BASE;

// ── Inline styles ──────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #FEF9E7 0%, #FFFBEB 55%, #ffffff 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
  },
  logoWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
    gap: '8px',
  },
  logoMark: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoText: {
    fontFamily: '"Fraunces", serif',
    fontSize: '26px',
    fontWeight: '700',
    color: '#0D1F3C',
    letterSpacing: '-0.5px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(13,31,60,0.08), 0 1px 4px rgba(13,31,60,0.06)',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '420px',
  },
  cardTitle: {
    fontFamily: '"Fraunces", serif',
    fontSize: '22px',
    fontWeight: '700',
    color: '#0D1F3C',
    marginBottom: '4px',
    marginTop: 0,
  },
  cardSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '28px',
    marginTop: 0,
  },
  formGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#0D1F3C',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #E5E9F0',
    fontSize: '14px',
    color: '#0D1F3C',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  inputFocus: {
    borderColor: '#1A6FD6',
    boxShadow: '0 0 0 3px rgba(26,111,214,0.12)',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: '10px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#DC2626',
    fontSize: '13.5px',
    marginBottom: '20px',
    lineHeight: '1.4',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    borderRadius: '10px',
    background: '#1A6FD6',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background 0.15s',
    letterSpacing: '0.01em',
  },
  submitBtnDisabled: {
    background: '#93B8E8',
    cursor: 'not-allowed',
  },
  footer: {
    marginTop: '28px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#9CA3AF',
  },
  footerLink: {
    color: '#1A6FD6',
    textDecoration: 'none',
    fontWeight: '500',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.7s linear infinite',
  },
};

// ── Logo SVG ──────────────────────────────────────────────────────────────────

const SuccesslaneLogo = () => (
  <div style={styles.logoMark}>
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="#1A6FD6" />
      <path
        d="M9 21L16 11L23 21"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.5 17L16 12L19.5 17"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span style={styles.logoText}>Successlane</span>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const RecruiterLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (sessionStorage.getItem('recruiter_auth') === '1') {
      navigate('/recruiter/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/recruiter-portal/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Invalid email or password.');
      }

      sessionStorage.setItem('recruiter_auth', '1');
      navigate('/recruiter/dashboard');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    ...styles.input,
    ...(focusedField === field ? styles.inputFocus : {}),
  });

  return (
    <>
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:wght@700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={styles.page}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <SuccesslaneLogo />
          <span style={{ fontSize: '13px', color: '#6B7280', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: '600' }}>
            Recruiter Portal
          </span>
        </div>

        {/* Card */}
        <div style={styles.card}>
          <h1 style={styles.cardTitle}>Welcome back</h1>
          <p style={styles.cardSubtitle}>Sign in to your recruiter account</p>

          {error && (
            <div style={styles.errorBox} role="alert">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                placeholder="you@company.com"
                style={inputStyle('email')}
                required
                autoComplete="email"
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="password" style={styles.label}>Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder="••••••••"
                style={inputStyle('password')}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitBtn,
                ...(loading ? styles.submitBtnDisabled : {}),
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#155FB8'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#1A6FD6'; }}
            >
              {loading ? (
                <>
                  <span style={styles.spinner} />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          Not a recruiter?{' '}
          <a href="https://successlane.io/recruiter" style={styles.footerLink}>
            Apply at successlane.io/recruiter
          </a>
        </div>
      </div>
    </>
  );
};

export default RecruiterLogin;
